import React, { useCallback, useMemo } from 'react';
import TodayAppointmentsCard from '../TodayAppointmentsCard';
import PendingConfirmationsCard from '../PendingConfirmationsCard';
import RecentUpdatesCard from '../RecentUpdatesCard';
import './OverviewMobilePanels.css';

const convertToDate = (dateValue) => {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) return dateValue;
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') return dateValue.toDate();
    if (dateValue?.seconds) return new Date(dateValue.seconds * 1000);
    const date = new Date(dateValue);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

export const OverviewMobileTodayPanel = ({
  appointments = [],
  loading = false,
  onAppointmentClick,
}) => {
  const todayAppointments = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return appointments.filter((appointment) => {
      if (!appointment || appointment.type === 'meeting') return false;
      const appointmentDate = convertToDate(appointment.dateTime);
      return appointmentDate
        && appointmentDate >= today
        && appointmentDate < tomorrow
        && !appointment.archived;
    });
  }, [appointments]);

  return (
    <div className="overview-mobile-panel-page">
      <TodayAppointmentsCard
        todayAppointments={todayAppointments}
        loading={loading}
        onAppointmentClick={onAppointmentClick}
      />
    </div>
  );
};

export const OverviewMobilePendingPanel = ({
  appointments = [],
  loading = false,
  onAppointmentClick,
}) => {
  const pendingAppointments = useMemo(() => (
    appointments.filter((appointment) => {
      if (!appointment || appointment.type === 'meeting') return false;

      try {
        const appointmentDate = convertToDate(appointment.dateTime);
        const status = String(appointment.status || '').toLowerCase();
        const awaitingClient =
          appointment.clientConfirmed !== true
          && (status === 'confirmed' || status === 'pending')
          && status !== 'pending_payment'
          && status !== 'processing';

        return awaitingClient
          && appointmentDate
          && !Number.isNaN(appointmentDate.getTime())
          && appointmentDate > new Date()
          && !appointment.archived;
      } catch {
        return false;
      }
    })
  ), [appointments]);

  return (
    <div className="overview-mobile-panel-page">
      <PendingConfirmationsCard
        pendingAppointments={pendingAppointments}
        loading={loading}
        onAppointmentClick={onAppointmentClick}
      />
    </div>
  );
};

export const OverviewMobileUpdatesPanel = ({
  updates = [],
  loading = false,
  companyId,
  onUpdateRead,
  onClearAllRead,
}) => (
  <div className="overview-mobile-panel-page">
    <RecentUpdatesCard
      updates={updates}
      loading={loading}
      companyId={companyId}
      onUpdateRead={onUpdateRead}
      onClearAllRead={onClearAllRead}
    />
  </div>
);

export const useAppointmentMobileNavCounts = (appointments = []) => {
  const getTodayAppointments = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    return appointments.filter((appointment) => {
      if (!appointment || appointment.type === 'meeting') return false;
      const appointmentDate = convertToDate(appointment.dateTime);
      return appointmentDate
        && appointmentDate >= today
        && appointmentDate < tomorrow
        && !appointment.archived;
    });
  }, [appointments]);

  const getPendingAppointments = useCallback(() => (
    appointments.filter((appointment) => {
      if (!appointment || appointment.type === 'meeting') return false;

      try {
        const appointmentDate = convertToDate(appointment.dateTime);
        const status = String(appointment.status || '').toLowerCase();
        const awaitingClient =
          appointment.clientConfirmed !== true
          && (status === 'confirmed' || status === 'pending')
          && status !== 'pending_payment'
          && status !== 'processing';

        return awaitingClient
          && appointmentDate
          && !Number.isNaN(appointmentDate.getTime())
          && appointmentDate > new Date()
          && !appointment.archived;
      } catch {
        return false;
      }
    })
  ), [appointments]);

  return useMemo(() => ({
    todayCount: getTodayAppointments().length,
    pendingCount: getPendingAppointments().length,
  }), [getPendingAppointments, getTodayAppointments]);
};
